/* eslint-disable max-len */
import {
  Component,
  ComponentInterface,
  h,
  Prop,
  Element,
  Event,
  EventEmitter,
} from '@stencil/core';
import debounce from 'debounce';
import { versionComponent } from '../../global/inject-dependencies';

export interface UiContainerRefs {
  markenFormShape: HTMLElement;
  container: HTMLElement;
}

export interface ContainerMetrics {
  padding: number;
  borderRadiusCorrection: number;
}

@Component({
  tag: 'ui-container',
  styleUrl: 'ui-container.scss',
  shadow: true,
})
export class UiContainer implements ComponentInterface {
  @Element() el: HTMLElement;

  /**
   * Defines the background and border styles for each different existing container variant. Check story file for possible combinations with "stylized" according to CD.
   */
  @Prop() variant:
    | 'primary'
    | 'primary100'
    | 'secondary100'
    | 'secondary200'
    | 'border'
    | 'white'
    | 'white-outline-dark'
    | 'outline-white'
    | 'outline-white-dark'
    | 'outline-primary'
    | 'outline-accent' = 'primary';

  /**
   * Checks whether the component should extend to the full page's width or remain with its default width.
   */
  @Prop({ reflect: true }) fullWidth: boolean;

  /**
   * Changes the container's to the brand shape (Markenform). Check story file for possible combinations with "variant" according to CD.
   */
  @Prop() stylized: boolean;

  /**
   * Changes the container's opacity. If *true*, turns the component opaque, otherwise it remains with its default opacity.
   */
  @Prop() opaque: boolean;

  /**
   * Either removes or adds padding to the container. The default value is *false* which means, the default component has padding.
   */
  @Prop() noPadding = false;

  /**
   * Emitted when the component has loaded correctly.
   */
  @Event({ eventName: 'ready' }) ready: EventEmitter;

  refs: UiContainerRefs = {
    markenFormShape: null,
    container: null,
  };

  get backgroundModifier(): string {
    switch (this.variant) {
      case 'primary100':
        return 'container--primary-lighter';
      case 'secondary100':
        return 'container--secondary-lighter';
      case 'secondary200':
        return 'container--secondary-light';
      case 'border':
        return 'container--white-outline-dark';
      default:
        return `container--${this.variant}`;
    }
  }

  get containerMetrics(): ContainerMetrics {
    const desktopBreakpoint = 1024;
    const tabletBreakpoint = 768;
    const windowWidth = window.innerWidth;

    let padding = 20;
    let borderRadiusCorrection = 6;

    const $smallParentGridElement =
      this.el.closest('.xs-12.md-4, .xs-12.md-3, .xs-12.md-2') != null;

    // border radius 50px
    if (windowWidth >= tabletBreakpoint && !$smallParentGridElement) {
      borderRadiusCorrection = 10;
    }

    if (windowWidth >= desktopBreakpoint) {
      padding = 40;
    }

    return {
      padding,
      borderRadiusCorrection,
    };
  }

  private setInlineStyles() {
    const ratio = 8502.262 / 400;
    const containerWidth = this.refs.container.scrollWidth;

    const markenformHeight = (containerWidth / 100) * ratio;

    this.refs.markenFormShape.style.marginTop = `${markenformHeight / 2}px`;
    this.refs.markenFormShape.style.marginBottom = `${markenformHeight / 2}px`;
    this.refs.container.style.paddingTop = `${
      markenformHeight +
      (this.containerMetrics.padding -
        this.containerMetrics.borderRadiusCorrection)
    }px`;
    this.refs.container.style.paddingBottom = `${
      markenformHeight +
      (this.containerMetrics.padding -
        this.containerMetrics.borderRadiusCorrection)
    }px`;
  }

  private setInlineStylesOnResize() {
    window.addEventListener(
      'resize',
      debounce(this.setInlineStyles.bind(this), 100)
    );
  }

  componentDidLoad() {
    if (this.refs.markenFormShape) {
      this.setInlineStyles();
      this.refs.container.classList.add('container--stylized--initialized');
      this.setInlineStylesOnResize();
    }
    this.ready.emit();
    [].slice.call(this.el.children).forEach((child) => {
      if (child && typeof child.update === 'function') {
        try {
          child.update();
        } catch (err) {
          console.error('Error updating child:', child, err);
        }
      }
    });
  }

  render() {
    return (
      <div
        ref={(el) => {
          this.refs.container = el as HTMLInputElement;
        }}
        class={{
          container: true,
          [this.backgroundModifier]: true,
          'container--stylized': this.stylized,
          'container--fullwidth': this.fullWidth,
          'container--opaque': this.opaque,
          'container--no-padding': this.noPadding,
        }}
        part="ui-container"
      >
        <slot></slot>
        {this.stylized && this.fullWidth && (
          <div
            class="container--stylized__background"
            ref={(el) => {
              this.refs.markenFormShape = el as HTMLInputElement;
            }}
          ></div>
        )}
      </div>
    );
  }
}

versionComponent(UiContainer);
